<template>
  <div class="move-app flex min-h-screen">
    <!-- Sidebar -->
    <aside
      v-show="isMobileMenuOpen || !isMobile"
      class="move-surface flex flex-col fixed inset-y-0 left-0 z-40 border-r transition-all duration-300 ease-in-out md:translate-x-0 overscroll-contain overflow-hidden"
      :class="[
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        isSidebarCollapsed ? 'w-20' : 'w-72'
      ]"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <div
        class="flex items-center gap-3 px-4 py-4 border-b min-h-[4.25rem]"
        :class="isSidebarCollapsed ? 'justify-center px-2' : ''"
        style="border-color: var(--move-border)"
      >
        <img
          src="https://tiye.ams3.cdn.digitaloceanspaces.com/tiye_assets/TIYE-main-logo.png"
          alt="Tiye"
          class="h-7 w-auto flex-shrink-0"
          :class="isDark ? 'brightness-0 invert opacity-90' : 'opacity-90'"
        />
        <span
          v-show="!isSidebarCollapsed"
          class="text-xs font-bold tracking-[0.22em] move-text-faint uppercase whitespace-nowrap flex-1"
        >
          {{ isProductionSection ? 'Production' : 'Move' }}
        </span>
        <button
          v-if="!isMobile && !isSidebarCollapsed"
          type="button"
          class="move-theme-toggle !w-9 !h-9 shrink-0"
          aria-label="Collapse sidebar"
          @click="toggleSidebar"
        >
          <span class="material-icons text-lg">chevron_left</span>
        </button>
      </div>

      <button
        v-if="!isMobile && isSidebarCollapsed"
        type="button"
        class="move-theme-toggle !w-9 !h-9 mx-auto mt-3 shrink-0"
        aria-label="Expand sidebar"
        @click="toggleSidebar"
      >
        <span class="material-icons text-lg">menu_open</span>
      </button>

      <div class="px-3 pt-4 pb-2 flex-1 overflow-y-auto overflow-x-hidden" :class="isSidebarCollapsed ? 'px-2' : 'px-4'">
        <div
          v-show="!isSidebarCollapsed"
          class="text-[0.65rem] font-bold move-text-faint mb-3 tracking-widest uppercase"
        >
          {{ isProductionSection ? 'Production' : 'Menu' }}
        </div>
        <nav class="space-y-1">
          <router-link
            v-if="isProductionSection"
            to="/dashboard"
            class="move-nav-link"
            :class="isSidebarCollapsed ? 'justify-center !px-2 !gap-0' : ''"
            title="Back to Move"
            @click="handleMenuClick"
          >
            <span class="material-icons flex-shrink-0 text-[1.2rem]">arrow_back</span>
            <span v-show="!isSidebarCollapsed" class="flex-1 whitespace-nowrap">Back to Move</span>
          </router-link>
          <router-link
            v-if="productionId"
            to="/production/productions"
            class="move-nav-link"
            :class="isSidebarCollapsed ? 'justify-center !px-2 !gap-0' : ''"
            title="All productions"
            @click="handleMenuClick"
          >
            <span class="material-icons flex-shrink-0 text-[1.2rem]">theaters</span>
            <span v-show="!isSidebarCollapsed" class="flex-1 whitespace-nowrap">All productions</span>
          </router-link>
          <router-link
            v-for="item in visibleNavItems"
            :key="item.to"
            v-slot="{ href, navigate, isActive, isExactActive }"
            :to="item.to"
            custom
          >
            <a
              :href="href"
              class="move-nav-link"
              :class="[
                { hidden: item.adminOnly && !isAdmin },
                isSidebarCollapsed ? 'justify-center !px-2 !gap-0' : '',
                (item.exact ? isExactActive : isActive) ? 'router-link-active' : ''
              ]"
              :title="isSidebarCollapsed ? item.label : undefined"
              @click="(e) => { navigate(e); handleMenuClick() }"
            >
              <span class="material-icons flex-shrink-0 text-[1.2rem]">{{ item.icon }}</span>
              <span v-show="!isSidebarCollapsed" class="flex-1 whitespace-nowrap">
                {{ item.label }}
              </span>
              <span
                v-if="item.badge && !isSidebarCollapsed"
                class="px-2 py-0.5 text-xs rounded-full flex-shrink-0"
                style="background: var(--move-accent-soft); color: var(--move-accent)"
              >
                {{ item.badge }}
              </span>
            </a>
          </router-link>
        </nav>

        <div v-if="!isProductionSection" class="mt-6 pt-4 border-t" style="border-color: var(--move-border)">
          <div
            v-show="!isSidebarCollapsed"
            class="text-[0.65rem] font-bold move-text-faint mb-3 tracking-widest uppercase"
          >
            Boards
          </div>
          <div v-if="boards.length === 0 && !isSidebarCollapsed" class="px-2 py-3">
            <p class="text-xs move-text-faint leading-relaxed">
              No boards yet
            </p>
          </div>
          <div v-else class="space-y-1">
            <router-link
              v-for="board in boards.slice(0, 8)"
              :key="board.id"
              :to="`/board/${board.id}`"
              class="move-nav-link !py-2 text-sm"
              :class="isSidebarCollapsed ? 'justify-center !px-2 !gap-0' : '!px-2'"
              :title="isSidebarCollapsed ? board.name : undefined"
              @click="handleMenuClick"
            >
              <span class="h-2 w-2 rounded-full flex-shrink-0" style="background: var(--move-accent)" />
              <span v-show="!isSidebarCollapsed" class="truncate">
                {{ board.name }}
              </span>
            </router-link>
          </div>
        </div>
      </div>

      <div class="px-3 py-4 border-t" :class="isSidebarCollapsed ? 'px-2' : 'px-4'" style="border-color: var(--move-border)">
        <button
          type="button"
          class="move-btn-ghost w-full"
          :class="isSidebarCollapsed ? 'justify-center !px-2' : ''"
          :title="isSidebarCollapsed ? 'Logout' : undefined"
          @click="logout"
        >
          <span class="material-icons text-lg">logout</span>
          <span v-show="!isSidebarCollapsed" class="whitespace-nowrap">
            Logout
          </span>
        </button>
      </div>
    </aside>

    <!-- Main -->
    <div
      class="flex-1 flex flex-col min-h-screen transition-all duration-300 ease-in-out min-w-0"
      :class="isSidebarCollapsed ? 'md:ml-20' : 'md:ml-72'"
    >
      <header
        class="sticky top-0 z-30 move-surface border-b flex items-center justify-between gap-3 px-4 md:px-8 py-3.5 backdrop-blur-xl min-h-[4.25rem]"
        style="border-color: var(--move-border)"
      >
        <div class="flex items-center gap-3 min-w-0 flex-1">
          <button
            v-if="isMobile"
            type="button"
            class="move-theme-toggle shrink-0"
            aria-label="Toggle menu"
            @click="toggleMobileMenu"
          >
            <span class="material-icons">{{ isMobileMenuOpen ? 'close' : 'menu' }}</span>
          </button>
          <h1 class="text-lg md:text-xl font-bold move-text truncate">
            {{ currentPageTitle }}
          </h1>
        </div>

        <div class="flex items-center gap-2 md:gap-3 shrink-0">
          <MoveGlobalSearch ref="globalSearchRef" :is-admin="isAdmin" />
          <button
            v-if="isMobile"
            type="button"
            class="move-theme-toggle !w-9 !h-9"
            aria-label="Refresh"
            @click="refreshData"
          >
            <span class="material-icons text-lg" :class="{ 'animate-spin': isRefreshing }">refresh</span>
          </button>
          <button
            type="button"
            class="move-theme-toggle"
            :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
            @click="toggleTheme"
          >
            <span class="material-icons">{{ isDark ? 'light_mode' : 'dark_mode' }}</span>
          </button>

          <router-link to="/profile" class="move-user-chip" aria-label="Open profile">
            <img
              :src="currentUser?.avatar || `https://ui-avatars.com/api/?name=${getInitials(currentUser?.email || '')}&background=00E181&color=0a0f0d`"
              :alt="currentUser?.email"
              class="move-user-chip__avatar"
            />
            <span class="move-user-chip__meta hidden sm:block">
              <span class="move-user-chip__name">{{ displayName || 'My account' }}</span>
              <span class="move-user-chip__email">{{ currentUser?.email }}</span>
            </span>
            <span class="material-icons move-user-chip__chevron hidden sm:block">chevron_right</span>
          </router-link>
        </div>
      </header>

      <main class="flex-1 px-4 md:px-8 py-6 md:py-8 overscroll-contain min-w-0">
        <slot />
      </main>
    </div>

    <!-- Mobile overlay -->
    <div
      v-if="isMobileMenuOpen"
      class="fixed inset-0 z-30 md:hidden backdrop-blur-sm"
      style="background: var(--move-overlay)"
      @click="closeMobileMenu"
    />

  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { moveApi } from '../services/moveApi'
import { useMoveTheme } from '../composables/useMoveTheme'
import MoveGlobalSearch from '../components/MoveGlobalSearch.vue'
import { productionWorkspaceNav } from '../config/productionNavigation'

const router = useRouter()
const route = useRoute()
const store = useStore()
const { isDark, toggleTheme } = useMoveTheme()

const boards = ref([])
const myTasksCount = ref(0)
const isMobileMenuOpen = ref(false)
const isRefreshing = ref(false)
const isMobile = ref(window.innerWidth < 768)
const isSidebarCollapsed = ref(false)
const globalSearchRef = ref(null)

const currentUser = computed(() => store.state.auth.user)
const isAdmin = computed(() => store.getters['auth/isMoveAdmin'])
const hasProductionAccess = computed(() => store.getters['auth/hasProductionAccess'])
const isProductionAdmin = computed(() => store.getters['auth/isProductionAdmin'])
const displayName = computed(() => store.getters['auth/displayName'])
const isProductionSection = computed(() => route.path.startsWith('/production'))
const productionId = computed(() => route.params.productionId || null)

const touchStartX = ref(0)
const touchEndX = ref(0)
const minSwipeDistance = 50

const menuItems = computed(() => [
  { to: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
  { to: '/my-tasks', label: 'My Tasks', icon: 'task_alt', badge: myTasksCount.value || null },
  { to: '/boards', label: 'Boards', icon: 'view_kanban', badge: boards.value.length || null },
  { to: '/production', label: 'Production', icon: 'movie_filter', hidden: !hasProductionAccess.value },
  { to: '/users', label: 'Users', icon: 'group', adminOnly: true },
  { to: '/settings', label: 'Settings', icon: 'settings', adminOnly: true }
])

const productionNavItems = computed(() => {
  if (productionId.value) return []
  return productionWorkspaceNav.filter((item) => !item.adminOnly || isProductionAdmin.value)
})

const visibleNavItems = computed(() => {
  if (isProductionSection.value) return productionNavItems.value
  return menuItems.value.filter((item) => !item.hidden)
})

const currentPageTitle = computed(() => route.meta.title || 'Move')

const handleTouchStart = (e) => {
  touchStartX.value = e.touches[0].clientX
}

const handleTouchMove = (e) => {
  touchEndX.value = e.touches[0].clientX
}

const handleTouchEnd = () => {
  const swipeDistance = touchEndX.value - touchStartX.value
  if (Math.abs(swipeDistance) > minSwipeDistance) {
    if (swipeDistance > 0 && !isMobileMenuOpen.value) toggleMobileMenu()
    else if (swipeDistance < 0 && isMobileMenuOpen.value) closeMobileMenu()
  }
}

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  document.body.classList.toggle('mobile-menu-open', isMobileMenuOpen.value)
}

const handleMenuClick = () => {
  if (isMobile.value) closeMobileMenu()
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
  document.body.classList.remove('mobile-menu-open')
}

const refreshData = async () => {
  isRefreshing.value = true
  try {
    await fetchSidebarData()
  } finally {
    isRefreshing.value = false
  }
}

const getInitials = (email) => {
  const name = email.split('@')[0]
  const parts = name.split(/[._-]/)
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

const fetchSidebarData = async () => {
  if (!store.getters['auth/isAuthenticated']) return
  try {
    const [boardsResponse, tasksResponse] = await Promise.all([
      moveApi.listBoards(),
      moveApi.myTasks()
    ])
    boards.value = boardsResponse.data.data || []
    myTasksCount.value = (tasksResponse.data.data || []).length
  } catch (error) {
    console.error('Error fetching sidebar data:', error)
    boards.value = []
    myTasksCount.value = 0
  }
}

const logout = async () => {
  await store.dispatch('auth/logout')
  router.push('/login')
}

const handleResize = () => {
  isMobile.value = window.innerWidth < 768
  if (!isMobile.value) closeMobileMenu()
}

const toggleSidebar = () => {
  isSidebarCollapsed.value = !isSidebarCollapsed.value
  localStorage.setItem('sidebarCollapsed', isSidebarCollapsed.value)
}

onMounted(() => {
  const savedState = localStorage.getItem('sidebarCollapsed')
  if (savedState !== null) isSidebarCollapsed.value = savedState === 'true'
  fetchSidebarData()
  window.addEventListener('resize', handleResize)
  router.afterEach(() => {
    closeMobileMenu()
    fetchSidebarData()
    globalSearchRef.value?.invalidateIndex?.()
  })
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

.material-icons {
  font-family: 'Material Icons', sans-serif;
  font-weight: normal;
  font-style: normal;
  line-height: 1;
  display: inline-block;
  vertical-align: middle;
}

body.mobile-menu-open {
  overflow: hidden;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

.hover-lift {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.hover-lift:hover {
  transform: translateY(-1px);
}

.active-press:active {
  transform: scale(0.98);
}
</style>
